import React, { Suspense, lazy, ComponentType } from "react";
import { PageWrapper } from "../Base/PageWrapper/PageWrapper";
import { KeepAlive } from "react-activation";
import { useUserStore } from "../../modules/user/stores";
import PageLoading from "../PageLoading";

// 默认的加载动画
const DefaultLoadingFallback = <PageLoading init={true} />;

export function withLazyLoading<T extends Record<string, any>>(
  importFunc: () => Promise<{ default: ComponentType<T> }>,
  fallback: React.ReactNode = DefaultLoadingFallback
) {
  const LazyComponent = lazy(importFunc);

  return function LazyWrapper(props: T) {
    return (
      <Suspense fallback={fallback}>
        <LazyComponent {...(props as any)} />
      </Suspense>
    );
  };
}

// 带PageWrapper的懒加载组件高阶函数
export function withLazyLoadingAndPageWrapper<T extends Record<string, any>>(
  importFunc: () => Promise<{ default: ComponentType<T> }>,
  fallback: React.ReactNode = DefaultLoadingFallback
) {
  const LazyComponent = lazy(importFunc);

  return function LazyWrapperWithPageWrapper(props: T) {
    return (
      <Suspense fallback={fallback}>
        <PageWrapper>
          <LazyComponent {...(props as any)} />
        </PageWrapper>
      </Suspense>
    );
  };
}

// 预定义的懒加载页面
export const LazyHomePage = withLazyLoading(
  () => import("../../pages/HomePage")
);

export const KeepAliveHomePage = () => {
  const { userInfo } = useUserStore();
  const cacheKey = `HomePage-${userInfo?.userId || "guest"}`;

  return (
    <KeepAlive name="HomePage" id="HomePage" cacheKey={cacheKey}>
      <LazyHomePage />
    </KeepAlive>
  );
};
export const LazyEventCenterPage = withLazyLoadingAndPageWrapper(
  () => import("../../pages/EventCenter")
);
export const KeepAliveEventCenterPage = () => {
  const { userInfo } = useUserStore();
  const cacheKey = `EventCenterPage-${userInfo?.userId || "guest"}`;

  return (
    <KeepAlive
      name="EventCenterPage"
      id="EventCenterPage"
      cacheKey={cacheKey}
      // autoFreeze: false 防止状态冻结，确保数据更新
      autoFreeze={false}
    >
      <LazyEventCenterPage />
    </KeepAlive>
  );
};

export const LazyAgentCenterPage = withLazyLoadingAndPageWrapper(
  () => import("../../pages/AgentCenter")
);
export const KeepAliveAgentCenterPage = () => {
  const { userInfo } = useUserStore();
  const cacheKey = `AgentCenterPage-${userInfo?.userId || "guest"}`;

  return (
    <KeepAlive
      name="AgentCenterPage"
      id="AgentCenterPage"
      cacheKey={cacheKey}
      autoFreeze={false}
    >
      <LazyAgentCenterPage />
    </KeepAlive>
  );
};

export const LazyInviteFriendsPage = withLazyLoadingAndPageWrapper(
  () => import("../../pages/InviteFriends")
);
export const KeepAliveInviteFriendsPage = () => {
  return <LazyInviteFriendsPage />;
};

export const LazyVIPPage = withLazyLoadingAndPageWrapper(
  () => import("../../pages/VIP")
);
export const KeepAliveVIPPage = () => {
  const { userInfo } = useUserStore();
  const vipLevel = userInfo?.vipLevel?.level || 0;
  const cacheKey = `VIPPage-${userInfo?.userId || "guest"}-${vipLevel}`;

  return (
    <KeepAlive
      name="VIPPage"
      id="VIPPage"
      cacheKey={cacheKey}
      autoFreeze={false}
    >
      <LazyVIPPage />
    </KeepAlive>
  );
};

export const LazyProfilePage = withLazyLoadingAndPageWrapper(
  () => import("../../pages/Profile")
);
export const KeepAliveProfilePage = () => {
  const { userInfo } = useUserStore();
  const cacheKey = `ProfilePage-${userInfo?.userId || "guest"}`;

  return (
    <KeepAlive
      name="ProfilePage"
      id="ProfilePage"
      cacheKey={cacheKey}
      autoFreeze={false}
    >
      <LazyProfilePage />
    </KeepAlive>
  );
};

// 懒加载路由组件
export function createLazyRoute(
  importFunc: () => Promise<{ default: ComponentType<any> }>
) {
  return withLazyLoading(importFunc);
}

// 带PageWrapper的懒加载路由组件
export function createLazyRouteWithPageWrapper(
  importFunc: () => Promise<{ default: ComponentType<any> }>
) {
  return withLazyLoadingAndPageWrapper(importFunc);
}
